import React from 'react';
import './HowItWorks.css';
import placeholderImage from '../assets/placeholder-image.png';

interface HowItWorksProps {
  title?: string;
  subtitle?: string;
  description?: string;
  features?: string[];
  additionalText?: string;
  imageAlt?: string;
  variant?: 'credit-assessment' | 'ecommerce' | 'general';
}

const HowItWorks: React.FC<HowItWorksProps> = ({
  title = "Advanced Credit Intelligence",
  subtitle = "How It Works",
  description = "Our platform revolutionizes traditional credit assessment by analyzing the complex web of relationships between entities",
  features = [
    "Identifies hidden risk patterns through network analysis of business relationships",
    "Provides real-time risk scoring based on direct and indirect entity connections",
    "Enables proactive risk management through early warning signals",
    "Integrates seamlessly with existing credit decision workflows"
  ],
  additionalText = "By examining the broader ecosystem of relationships, our solution provides deeper insights into creditworthiness that traditional models cannot detect, enabling more accurate and confident lending decisions.",
  imageAlt = "Credit Assessment Network Analytics Visualization",
  variant = 'general'
}) => {
  return (
    <section className={`how-it-works-section ${variant}`}>
      {/* Background Elements */}
      <div className="section-background">
        <div className="gradient-overlay"></div>
        <div className="particles-background"></div>
      </div>
      
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-icon">⚡</span>
            <span>{subtitle.toUpperCase()}</span>
          </div>
          <h2 className="section-title">
            <span className="title-highlight">{title}</span>
          </h2>
          <p className="section-subtitle">{description}</p>
        </div>

        {/* Main Content Card */}
        <div className="main-content-card">
          <div className="card-background-pattern"></div>
          
          <div className="content-layout">
            {/* Features List */}
            <div className="features-section">
              <div className="features-header">
                <div className="features-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9L16 14.74L17.18 21.02L12 18.77L6.82 21.02L8 14.74L2 9L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
                <h3>Key Capabilities</h3>
              </div>
              
              <ul className="feature-list">
                {features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <div className="feature-bullet">
                      <svg viewBox="0 0 16 16" className="feature-icon">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits Stats */}
            <div className="benefits-section">
              <div className="benefits-header">
                <div className="benefits-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Key Benefits</h3>
              </div>
              
              <div className="benefits-grid">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="benefit-content">
                    <div className="benefit-value">INSTANT</div>
                    <div className="benefit-label">Processing Speed</div>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="benefit-content">
                    <div className="benefit-value">ACCURATE</div>
                    <div className="benefit-label">Risk Assessment</div>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 17L12 22L22 17M2 12L12 17L22 12M12 2L22 7L12 12L2 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="benefit-content">
                    <div className="benefit-value">SEAMLESS</div>
                    <div className="benefit-label">Integration</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {additionalText && (
            <div className="additional-content">
              <p>{additionalText}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
